import { AppStore, appStore, authStore, AuthStore, HubContext, MessageBus } from '@tom-ui/utils'
import { YardBlockStore } from '@yard/stores/YardBlockStore'
import { createContext, ReactNode, useContext, useEffect, useState } from 'react'
import { setupInterceptors } from './app/http-client'
import { YardPositionSelectorStore } from './components/YardPositionSelector/stores/yard-position-selector.store'
import { DGSegregationRuleDataStore } from './features/dg-segregation-rules/stores/dg-segregation-rule.data-store'
import { DGSegregationRulesTableUIStore } from './features/dg-segregation-rules/stores/dg-segregation-rules-table.ui-store'
import { DGStackingRuleStore } from './features/dg-stacking-rules/stores/dg-stacking-rule.store'
import { DGStackingRulesTableUIStore } from './features/dg-stacking-rules/stores/dg-stacking-rules-table.ui-store'
import { ContainerLengthBayMappingStore } from './features/container-length-bay-mappings/stores/container-length-bay-mapping.store'
import { ContainerLengthBayMappingsTableUIStore } from './features/container-length-bay-mappings/stores/container-length-bay-mappings-table.ui-store'
import { YardIssueStore, YardIssueUIStore } from './features/issue'
import { eventsToListen } from './messages/eventsTypes'
import { roadNetworkService } from './services/road-network.service'
import { AllocationRuleStore } from './stores/allocation-rule.store'
import { BerthStore } from './stores/berth.store'
import { CapacitySettingsStore } from './stores/CapacitySettingsStore'
import { CarrierVisitStore } from './stores/carrier-visit.store'
import { CompanyStore } from './stores/CompanyStore'
import { HousekeepingPlanStore } from './stores/housekeeping-plan.store'
import { JourneyStore } from './stores/journey.store'
import { LayoutObjectStore } from './stores/LayoutObjectStore'
import { OccupancyStore } from './stores/OccupancyStore'
import { PierStore } from './stores/pier.store'
import { RailTrackStore } from './stores/rail-track.store'
import { RoadNetworkStore } from './stores/road-network.store'
import { SnackbarStore } from './stores/snackbar.store'

export const messageBus = new MessageBus()

interface IAppStore {
  appStore: AppStore
  authStore: AuthStore
  carrierVisitStore: CarrierVisitStore
  journeyStore: JourneyStore
  layoutObjectStore: LayoutObjectStore
  occupancyStore: OccupancyStore
  yardBlockStore: YardBlockStore
  yardPositionSelectorStore: YardPositionSelectorStore
  berthStore: BerthStore
  railTrackStore: RailTrackStore
  pierStore: PierStore
  allocationRuleStore: AllocationRuleStore
  yardIssueUIStore: YardIssueUIStore
  roadNetworkStore: RoadNetworkStore
  capacitySettingsStore: CapacitySettingsStore
  dgSegregationRulesTableUIStore: DGSegregationRulesTableUIStore
  dgStackingRulesTableUIStore: DGStackingRulesTableUIStore
  containerLengthBayMappingsTableUIStore: ContainerLengthBayMappingsTableUIStore
  companyStore: CompanyStore
  housekeepingPlanStore: HousekeepingPlanStore
}

const createYardStores = (): { store: IAppStore; dispose: () => void } => {
  const hubContext = new HubContext('/yardHub', messageBus)
  eventsToListen.forEach(c => hubContext.registerPassThroughConnection(c))
  hubContext.startConnection()

  const carrierVisitStore = new CarrierVisitStore(messageBus)
  const journeyStore = new JourneyStore(messageBus)
  const layoutObjectStore = new LayoutObjectStore(messageBus)
  const occupancyStore = new OccupancyStore(messageBus, journeyStore)
  const yardBlockStore = new YardBlockStore(messageBus)
  const yardPositionSelectorStore = new YardPositionSelectorStore(yardBlockStore)
  const berthStore = new BerthStore(messageBus)
  const railTrackStore = new RailTrackStore(messageBus)
  const pierStore = new PierStore(messageBus)
  const allocationRuleStore = new AllocationRuleStore(messageBus)
  const yardIssueStore = new YardIssueStore(messageBus)
  const yardIssueUIStore = new YardIssueUIStore(yardIssueStore, journeyStore, yardBlockStore)
  const roadNetworkStore = new RoadNetworkStore(roadNetworkService, messageBus)
  const capacitySettingsStore = new CapacitySettingsStore()
  const companyStore = new CompanyStore()
  const housekeepingPlanStore = new HousekeepingPlanStore(messageBus)

  const snackbarStore = new SnackbarStore()
  const dgSegregationRuleDataStore = new DGSegregationRuleDataStore()
  const dgSegregationRulesTableUIStore = new DGSegregationRulesTableUIStore(
    dgSegregationRuleDataStore,
    snackbarStore,
  )

  const dgStackingRuleStore = new DGStackingRuleStore()
  const dgStackingRulesTableUIStore = new DGStackingRulesTableUIStore(
    dgStackingRuleStore,
    snackbarStore,
  )

  const containerLengthBayMappingStore = new ContainerLengthBayMappingStore()
  const containerLengthBayMappingsTableUIStore = new ContainerLengthBayMappingsTableUIStore(
    containerLengthBayMappingStore,
    snackbarStore,
  )

  const store: IAppStore = {
    appStore,
    authStore,
    carrierVisitStore,
    journeyStore,
    layoutObjectStore,
    occupancyStore,
    yardBlockStore,
    yardPositionSelectorStore,
    berthStore,
    railTrackStore,
    pierStore,
    allocationRuleStore,
    yardIssueUIStore,
    roadNetworkStore,
    capacitySettingsStore,
    dgSegregationRulesTableUIStore,
    dgStackingRulesTableUIStore,
    containerLengthBayMappingsTableUIStore,
    companyStore,
    housekeepingPlanStore,
  }

  const dispose = () => {
    carrierVisitStore.dispose()
    journeyStore.dispose()
    layoutObjectStore.dispose()
    occupancyStore.dispose()
    yardBlockStore.dispose()
    berthStore.dispose()
    railTrackStore.dispose()
    pierStore.dispose()
    allocationRuleStore.dispose()
    yardIssueStore.dispose()
    roadNetworkStore.dispose()
    housekeepingPlanStore.dispose()
  }

  setupInterceptors(appStore)

  return { store, dispose }
}

const YardContext = createContext<IAppStore>(null as any)

export const YardStoreProvider = ({ children }: { children: ReactNode }) => {
  const [{ store, dispose }] = useState(createYardStores)

  useEffect(() => {
    return dispose
  }, [dispose])

  return <YardContext.Provider value={store}>{children}</YardContext.Provider>
}

export const useYardStores = () => useContext(YardContext)
